/**
 * V1 Surveys List Page
 * 
 * Shows all available surveys for a V1 event.
 * Groups surveys by type (Event vs Session).
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Clock, MessageSquare, CheckCircle, FileText, Users, AlertCircle, UserX, Lock } from 'lucide-react';
import { useEventContext } from '../../contexts/EventContext';
import { v1SurveysApi } from '../api/v1SurveysApi';
import { decodeHtmlEntities } from '../../../utils/htmlUtils';

interface V1SurveyListItem {
  id: string;
  name: string;
  slug: string;
  description?: string;
  surveyableType: 'Event' | 'Session';
  surveyableName?: string;
  sessionSlug?: string;
  daySlug?: string;
  canBeTaken: boolean;
  hasAlreadyResponded: boolean;
  totalQuestions: number;
  anonymous: boolean;
  requireAccount: boolean;
  startsAt?: string;
  endsAt?: string;
}

export const SurveysPageV1: React.FC = () => {
  const navigate = useNavigate();
  const { eventSlug } = useEventContext();
  
  // Fetch surveys list
  const { data: surveys = [], isLoading, error } = useQuery({
    queryKey: ['v1-surveys-list', eventSlug],
    queryFn: () => v1SurveysApi.getSurveysList(eventSlug || ''),
    enabled: !!eventSlug,
  });
  
  // Memoize expensive calculations
  const surveyStats = useMemo(() => ({
    total: surveys.length,
    available: surveys.filter(s => s.canBeTaken && !s.hasAlreadyResponded).length,
    completed: surveys.filter(s => s.hasAlreadyResponded).length,
  }), [surveys]);
  
  // Memoize filtered arrays
  const eventSurveys = useMemo(
    () => surveys.filter(s => s.surveyableType === 'Event'),
    [surveys]
  );
  
  const sessionSurveys = useMemo(
    () => surveys.filter(s => s.surveyableType === 'Session'),
    [surveys]
  );
  
  const handleNavigate = (url: string) => {
    navigate(url);
  };
  
  // Enhanced survey card component
  const renderSurveyCard = (survey: V1SurveyListItem) => {
    const isAvailable = survey.canBeTaken;
    const needsAuth = survey.requireAccount && !window.currentAccount;
    
    // Generate URL based on survey type
    const surveyUrl = survey.surveyableType === 'Session' && survey.daySlug && survey.sessionSlug
      ? `/days/${survey.daySlug}/sessions/${survey.sessionSlug}/surveys/${survey.slug}`
      : `/surveys/${survey.slug}`;
    
    const handleClick = (e: React.MouseEvent) => {
      if (!isAvailable && !needsAuth) {
        e.preventDefault();
        return;
      }
      handleNavigate(surveyUrl);
    };
    
    return (
      <div 
        key={survey.id}
        className={`
          rounded-lg border transition-all bg-gray-50
          ${needsAuth 
            ? 'border-amber-200 hover:border-amber-300 hover:shadow-sm cursor-pointer' 
            : isAvailable 
            ? 'border-gray-200 hover:border-gray-300 hover:shadow-sm cursor-pointer' 
            : 'border-gray-100 cursor-not-allowed opacity-75'
          }
        `}
        onClick={handleClick}
        role="button"
        tabIndex={isAvailable || needsAuth ? 0 : -1}
        onKeyDown={(e) => e.key === 'Enter' && handleClick(e as any)}
        aria-label={`${survey.name} survey ${needsAuth ? '(authentication required)' : isAvailable ? '' : '(not available)'}`}
      >
        <div className="p-5">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1">
              {/* Icon */}
              <div className={`p-2 rounded-lg ${needsAuth ? 'bg-amber-100' : isAvailable ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <FileText className={`w-6 h-6 ${needsAuth ? 'text-amber-600' : isAvailable ? 'text-blue-600' : 'text-gray-400'}`} />
              </div>
              
              {/* Content */}
              <div className="flex-1">
                <h3 className={`text-base font-medium ${isAvailable ? 'text-gray-900' : 'text-gray-500'}`}>
                  {survey.name}
                </h3>
                
                {survey.surveyableType === 'Session' && survey.surveyableName && (
                  <p className="text-sm text-gray-500 mt-1">
                    Session: {survey.surveyableName}
                  </p>
                )}
                
                {survey.description && (
                  <p 
                    className="text-sm text-gray-600 mt-2 line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: survey.description }}
                  />
                )}
                
                {/* Metadata */}
                <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" />
                    {survey.totalQuestions} questions
                  </span>
                  
                  {survey.totalPages > 1 && (
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {survey.totalPages} pages
                    </span>
                  )}
                  
                  {survey.anonymous && (
                    <span className="flex items-center gap-1">
                      <UserX className="w-4 h-4" />
                      Anonymous
                    </span>
                  )}
                  
                  {survey.requireAccount && !window.currentAccount && (
                    <span className="flex items-center gap-1 text-amber-600">
                      <Lock className="w-4 h-4" />
                      Account required
                    </span>
                  )}
                  
                  {survey.endsAt && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Closes {new Date(survey.endsAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Status Indicator */}
            <div className="ml-4 flex-shrink-0">
              {survey.hasAlreadyResponded ? (
                <div className="flex items-center text-sm text-green-600">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Completed
                </div>
              ) : !isAvailable ? (
                <div className="text-sm text-gray-500">Not available</div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Section rendering helper
  const renderSurveySection = (
    title: string,
    icon: React.ReactNode,
    surveys: V1SurveyListItem[]
  ) => {
    if (surveys.length === 0) return null;
    
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          {icon}
          <span className="ml-2">{title}</span>
        </h2>
        <div className="space-y-4">
          {surveys.map(renderSurveyCard)}
        </div>
      </div>
    );
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="bg-white/95 rounded-lg shadow-sm p-6">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="space-y-4">
                <div className="h-24 bg-gray-200 rounded"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Surveys</h2>
            <p className="text-gray-600 mb-4">
              There was an error loading the surveys. Please try again later.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Empty state
  if (surveys.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="bg-gray-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <FileText className="h-10 w-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Surveys Available</h2>
            <p className="text-gray-600 max-w-sm mx-auto">
              There are no surveys available for this event at the moment. 
              Please check back later.
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white/95 rounded-lg shadow-sm p-6">
          <div className="space-y-6">
            {/* Header with statistics */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Surveys</h1>
                  <p className="text-gray-600 mt-1">
                    Help us improve your experience by completing these surveys
                  </p>
                </div>
                
                <div className="flex gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-lg">{surveyStats.total}</div>
                    <div className="text-gray-600">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-lg text-blue-600">{surveyStats.available}</div>
                    <div className="text-gray-600">Available</div>
                  </div>
                  {surveyStats.completed > 0 && (
                    <div className="text-center">
                      <div className="font-semibold text-lg text-green-600">{surveyStats.completed}</div>
                      <div className="text-gray-600">Completed</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Survey sections */}
            {renderSurveySection(
              'Event Surveys',
              <Calendar className="w-5 h-5 text-gray-600" />,
              eventSurveys
            )}
            
            {renderSurveySection(
              'Session Surveys',
              <Users className="w-5 h-5 text-gray-600" />,
              sessionSurveys
            )}
          </div>
        </div>
      </div>
    </div>
  );
};